import React, { useState, useEffect, useRef } from 'react';
import { profileAPI } from '../services/api.ts';
import { API_URL, API_ENDPOINTS } from '../config/constants.ts';
import { toast } from 'react-toastify';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { Capacitor } from '@capacitor/core';

// Feed interfaces - Based on TimeLine table structure
export interface FeedAttachment {
  id: string | number;
  type: 'pdf' | 'image' | 'youtube' | 'video';
  url: string;
  thumbnailUrl?: string;
  title?: string;
  fileName?: string;
  fileSize?: number;
  mimeType?: string;
  youtubeVideoId?: string;
  youtubeThumbnailUrl?: string;
  youtubeDuration?: string;
  feedItemId?: string | number; // Store the feed item ID for PDF downloads
  provider?: 'youtube' | 'facebook' | 'generic';
}

export interface FeedItem {
  id: string | number;
  title: string;
  description?: string; // Rich text HTML content (may contain images)
  createdAt: string;
  updatedAt?: string;
  eventDate?: string;
  author?: {
    name: string;
  };
  updatedBy?: string;
  formTypeId?: number;
  isShowAllTeam?: boolean;
  attachments?: FeedAttachment[];
}

const Feed: React.FC = () => {
  const [feedItems, setFeedItems] = useState<FeedItem[]>([]);
  const [feedLoading, setFeedLoading] = useState(false);
  const [feedPageNumber, setFeedPageNumber] = useState(1);
  const [feedHasMore, setFeedHasMore] = useState(true);
  const [feedError, setFeedError] = useState<string | null>(null);
  const [feedServerError, setFeedServerError] = useState(false);
  const feedObserverRef = useRef<IntersectionObserver | null>(null);
  const feedLoadMoreRef = useRef<HTMLDivElement | null>(null);
  const hasInitialFetchRef = useRef(false);
  const [loadingPdfId, setLoadingPdfId] = useState<string | number | null>(null);

  // Helper function to extract YouTube video ID from URL
  const extractYouTubeVideoId = (url: string): string | null => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /youtube\.com\/watch\?.*v=([^&\n?#]+)/
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) return match[1];
    }
    return null;
  };

  // Helper function to format file size
  const formatFileSize = (bytes?: number): string => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Handle PDF view/download
  const handlePdfView = async (feedItemId: string | number, fileName: string) => {
    if (!feedItemId || !fileName) return;
    
    setLoadingPdfId(`${feedItemId}-pdf`);
    try {
      const blob = await profileAPI.getTimeLineFile(feedItemId, fileName);
      
      // Check if running on native platform (Android/iOS)
      if (Capacitor.isNativePlatform()) {
        // Convert blob to base64
        const reader = new FileReader();
        const base64Data = await new Promise<string>((resolve, reject) => {
          reader.onloadend = () => {
            const base64String = reader.result as string;
            // Remove data URL prefix (data:application/pdf;base64,)
            const base64 = base64String.split(',')[1];
            resolve(base64);
          };
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });

        // Save PDF to cache directory
        const sanitizedFileName = fileName.replace(/[^a-zA-Z0-9.-]/g, '_');
        const filePath = `pdf_${feedItemId}_${sanitizedFileName}`;
        
        await Filesystem.writeFile({
          path: filePath,
          data: base64Data,
          directory: Directory.Cache,
        });

        // Get the file URI
        const fileUri = await Filesystem.getUri({
          path: filePath,
          directory: Directory.Cache,
        });

        // Try to share/open the PDF with external app
        try {
          await Share.share({
            title: fileName,
            url: fileUri.uri,
            dialogTitle: 'Open PDF',
          });
        } catch (shareError: any) {
          // If share fails, try opening with intent (Android)
          if (Capacitor.getPlatform() === 'android') {
            // Use Capacitor Browser plugin or open via intent
            window.open(fileUri.uri, '_system');
          } else {
            throw shareError;
          }
        }
      } else {
        // Web platform - use blob URL
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (error: any) {
      console.error('Error loading PDF:', error);
      toast.error('Failed to open PDF document. Please try again.');
    } finally {
      setLoadingPdfId(null);
    }
  };

  // Helper function to format date
  const formatFeedDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString(undefined, { 
        day: 'numeric', 
        month: 'short', 
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined 
      });
    }
  };

  // Transform backend response to frontend format
  const transformFeedItem = (item: any): FeedItem => {
    const attachments: FeedAttachment[] = [];
    
    // Transform pdfFileName to attachment if it exists
    const pdfFileName = item.pdfFileName || item.PdfFileName;
    const feedItemId = item.id || item.Id;
    if (pdfFileName) {
      attachments.push({
        id: `${feedItemId}-pdf`,
        type: 'pdf',
        url: pdfFileName.startsWith('http') 
          ? pdfFileName 
          : `${API_URL}/files/${pdfFileName}`, // Fallback URL
        fileName: pdfFileName,
        title: (item.title || item.Title) || 'PDF Document',
        mimeType: 'application/pdf',
        feedItemId: feedItemId // Store feed item ID for API call
      });
    }
    
    // Transform videoUrl to attachment if present
    const videoUrl = item.videoUrl || item.VideoUrl;
    if (videoUrl) {
      const youTubeId = extractYouTubeVideoId(videoUrl);
      attachments.push({
        id: `${feedItemId}-video`,
        type: youTubeId ? 'youtube' : 'video',
        url: videoUrl,
        title: (item.title || item.Title) || 'Video',
        youtubeVideoId: youTubeId || undefined,
        provider: youTubeId ? 'youtube' : (videoUrl.includes('facebook.com') ? 'facebook' : 'generic')
      });
    }
    
    // Handle both camelCase and PascalCase for compatibility
    const id = item.id || item.Id;
    const title = item.title || item.Title || '';
    const details = item.details || item.Details;
    const createdDateTime = item.createdDateTime || item.CreatedDateTime;
    const updatedDateTime = item.updatedDateTime || item.UpdatedDateTime;
    const eventDate = item.eventDate || item.EventDate;
    const createdBy = item.createdBy || item.CreatedBy;
    const updatedBy = item.updatedBy || item.UpdatedBy;
    const formTypeId = item.formTypeId || item.FormTypeId;
    const isShowAllTeam = item.isShowAllTeam !== undefined ? item.isShowAllTeam : (item.IsShowAllTeam || false);
    
    return {
      id: id,
      title: title,
      description: details || undefined,
      createdAt: createdDateTime ? new Date(createdDateTime).toISOString() : new Date().toISOString(),
      updatedAt: updatedDateTime ? new Date(updatedDateTime).toISOString() : undefined,
      eventDate: eventDate ? new Date(eventDate).toISOString() : undefined,
      author: createdBy ? { name: createdBy } : undefined,
      updatedBy: updatedBy || undefined,
      formTypeId: formTypeId || undefined,
      isShowAllTeam: isShowAllTeam,
      attachments: attachments.length > 0 ? attachments : undefined
    };
  };

  // Fetch feed items with pagination
  const fetchFeed = async (pageNumber: number, isLoadMore: boolean = false) => {
    if (feedLoading || (!isLoadMore && !feedHasMore) || feedServerError) return;
    
    setFeedLoading(true);
    setFeedError(null);
    
    try {
      const response = await profileAPI.getFeed({
        PageNumber: pageNumber,
        PageSize: 10
      });
      
      const rawItems = response.data || [];
      const transformedItems: FeedItem[] = rawItems.map(transformFeedItem);
      
      const pageTotal = response.pageTotal || 0;
      const currentPage = response.currentPage || pageNumber;
      const hasMore = currentPage < pageTotal;
      
      if (isLoadMore) {
        setFeedItems(prev => [...prev, ...transformedItems]);
      } else {
        const sorted = [...transformedItems].sort((a, b) => {
          if (a.isShowAllTeam && !b.isShowAllTeam) return -1;
          if (!a.isShowAllTeam && b.isShowAllTeam) return 1;
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        });
        setFeedItems(sorted);
      }
      
      setFeedHasMore(hasMore);
      setFeedPageNumber(pageNumber);
      setFeedServerError(false);
    } catch (err: any) {
      console.error('Error fetching feed:', err);
      
      const isServerError = err.response?.status === 500 || err.status === 500;
      
      if (isServerError) {
        setFeedServerError(true);
        setFeedHasMore(false);
        setFeedError('Server error. Please try again later.');
        if (!isLoadMore) {
          toast.error('Unable to load feed. Server error occurred.');
        }
      } else {
        setFeedError(err.message || 'Failed to load feed');
        if (!isLoadMore) {
          toast.error('Failed to load feed items');
        }
      }
    } finally {
      setFeedLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    if (hasInitialFetchRef.current) return;
    hasInitialFetchRef.current = true;
    fetchFeed(1, false);
  }, []);

  // Infinite scroll observer
  useEffect(() => {
    if (!feedLoadMoreRef.current) return;
    if (feedServerError) return;

    if (feedObserverRef.current) {
      feedObserverRef.current.disconnect();
    }

    feedObserverRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && feedHasMore && !feedLoading && !feedServerError) {
          fetchFeed(feedPageNumber + 1, true);
        }
      },
      { threshold: 0.1 }
    );

    feedObserverRef.current.observe(feedLoadMoreRef.current);

    return () => {
      if (feedObserverRef.current) {
        feedObserverRef.current.disconnect();
      }
    };
  }, [feedHasMore, feedLoading, feedPageNumber, feedServerError]);

  return (
    <div className="mb-6">
      <div className="flex justify-between items-center mb-2">
        <h2 className="text-lg font-bold">Feed</h2>
      </div>
      
      {feedError && (
        <div className={`border rounded-lg p-3 mb-3 ${
          feedServerError 
            ? 'bg-red-50 border-red-200' 
            : 'bg-yellow-50 border-yellow-200'
        }`}>
          <div className="flex items-start gap-2">
            <svg className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
              feedServerError ? 'text-red-600' : 'text-yellow-600'
            }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1">
              <p className={`text-sm font-medium ${
                feedServerError ? 'text-red-800' : 'text-yellow-800'
              }`}>
                {feedError}
              </p>
              {feedServerError && (
                <p className="text-xs text-red-600 mt-1">
                  Further requests have been stopped. Please refresh the page to try again.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {feedItems.length === 0 && !feedLoading && !feedError && (
          <div className="bg-white shadow rounded-lg p-6 text-center text-gray-500">
            No feed items available
          </div>
        )}

        {feedItems.map((item) => {
          const attachments = item.attachments?.map(att => {
            if (att.type === 'youtube' && !att.youtubeVideoId && att.url) {
              const videoId = extractYouTubeVideoId(att.url);
              return { ...att, youtubeVideoId: videoId || undefined };
            }
            return att;
          }) || [];

          return (
            <div key={item.id} className={`bg-white shadow rounded-lg p-4 ${item.isShowAllTeam ? 'border-l-4 border-primary' : ''}`}>
              {/* Header */}
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-gray-900">{item.title}</h3>
                  </div>
                </div>
                <span className="text-xs text-gray-400">{formatFeedDate(item.createdAt)}</span>
              </div>

              {/* Description - Rich text content */}
              {item.description && (
                <div 
                  className="text-sm text-gray-600 mb-3 prose prose-sm max-w-none feed-content"
                  dangerouslySetInnerHTML={{ __html: item.description }}
                  style={{
                    wordBreak: 'break-word'
                  }}
                />
              )}

              {/* Attachments */}
              {attachments.length > 0 && (
                <div className="space-y-3 mt-3">
                  {attachments.map((attachment) => (
                    <div key={attachment.id} className="border border-gray-200 rounded-lg overflow-hidden">
                      {attachment.type === 'image' && (
                        <div>
                          <img
                            src={attachment.thumbnailUrl || attachment.url}
                            alt={attachment.title || 'Attachment'}
                            className="w-full h-auto max-h-64 object-cover"
                            onError={(e) => {
                              if (attachment.thumbnailUrl && (e.target as HTMLImageElement).src !== attachment.url) {
                                (e.target as HTMLImageElement).src = attachment.url;
                              }
                            }}
                          />
                          {attachment.title && (
                            <div className="p-2 bg-gray-50">
                              <p className="text-xs font-medium text-gray-700">{attachment.title}</p>
                              {attachment.fileSize && (
                                <p className="text-xs text-gray-500">{formatFileSize(attachment.fileSize)}</p>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {attachment.type === 'pdf' && (
                        <div className="p-4 flex items-center gap-3">
                          <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                            <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {attachment.title || attachment.fileName || 'PDF Document'}
                            </p>
                            {attachment.fileSize && (
                              <p className="text-xs text-gray-500">{formatFileSize(attachment.fileSize)}</p>
                            )}
                          </div>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              if (attachment.feedItemId && attachment.fileName) {
                                handlePdfView(attachment.feedItemId, attachment.fileName);
                              } else if (attachment.url) {
                                window.open(attachment.url, '_blank', 'noopener,noreferrer');
                              }
                            }}
                            disabled={loadingPdfId === attachment.id}
                            className="flex-shrink-0 px-4 py-2 bg-primary text-white text-sm font-medium rounded-md hover:bg-primary-dark transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                          >
                            {loadingPdfId === attachment.id ? (
                              <span className="inline-flex items-center">
                                <span className="inline-block animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></span>
                                Loading...
                              </span>
                            ) : (
                              'View'
                            )}
                          </button>
                        </div>
                      )}

                      {attachment.type === 'youtube' && (
                        <div>
                          {attachment.youtubeVideoId ? (
                            <div className="relative" style={{ paddingBottom: '56.25%', height: 0, overflow: 'hidden' }}>
                              <iframe
                                className="absolute top-0 left-0 w-full h-full"
                                src={`https://www.youtube.com/embed/${attachment.youtubeVideoId}`}
                                title={attachment.title || 'YouTube video'}
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                              />
                            </div>
                          ) : (
                            <div className="p-4">
                              <a
                                href={attachment.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-3 text-primary hover:underline"
                              >
                                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                                </svg>
                                <span className="text-sm font-medium">
                                  {attachment.title || 'Watch on YouTube'}
                                </span>
                              </a>
                            </div>
                          )}
                          {attachment.title && !attachment.youtubeVideoId && (
                            <div className="px-4 pb-2">
                              <p className="text-xs text-gray-500">{attachment.title}</p>
                            </div>
                          )}
                        </div>
                      )}

                      {attachment.type === 'video' && (
                        <div className="bg-black text-white">
                          {attachment.provider === 'facebook' ? (
                            <div className="relative" style={{ paddingBottom: '56.25%', height: 0, overflow: 'hidden' }}>
                              <iframe
                                className="absolute top-0 left-0 w-full h-full"
                                src={`https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(attachment.url)}&show_text=false&width=500`}
                                title={attachment.title || 'Facebook video'}
                                style={{ border: 'none', overflow: 'hidden' }}
                                scrolling="no"
                                frameBorder="0"
                                allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                                allowFullScreen
                              />
                            </div>
                          ) : (
                            <video controls className="w-full h-auto">
                              <source src={attachment.url} />
                              Your browser does not support the video tag.
                            </video>
                          )}
                          {attachment.title && (
                            <div className="p-2 text-sm font-medium bg-black bg-opacity-60">
                              {attachment.title}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {/* Loading indicator */}
        {feedLoading && feedItems.length === 0 && (
          <div className="bg-white shadow rounded-lg p-6 text-center">
            <div className="inline-block animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full"></div>
            <p className="text-sm text-gray-500 mt-2">Loading feed...</p>
          </div>
        )}

        {/* Load more trigger (for infinite scroll) */}
        {!feedServerError && (
          <div ref={feedLoadMoreRef} className="h-4">
            {feedLoading && feedItems.length > 0 && (
              <div className="text-center py-4">
                <div className="inline-block animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full"></div>
                <p className="text-xs text-gray-500 mt-2">Loading more...</p>
              </div>
            )}
            {!feedHasMore && feedItems.length > 0 && (
              <div className="text-center py-4 text-sm text-gray-500">
                No more items to load
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Feed;

